import { type IntlDateTimeFormats } from "vue-i18n";

const DEFAULT_TIMEZONE = "Europe/Berlin";

type LocaleDateTimeFormats = IntlDateTimeFormats[string];

// Formats with a time part but no `hour12`: the clock comes from the user's
// profile setting (useUserDateTimeFormatter) or, for visitors, the locale's
// default. Date order and separators come from the locale, so only the hour
// style differs: English writes "9:05 AM" (numeric still renders "09:05" on a
// 24-hour clock), the day-first locales keep "09:05".
function sharedFormats(
  timezone: string,
  hour: "numeric" | "2-digit"
): LocaleDateTimeFormats {
  return {
    dateTime: {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour,
      minute: "2-digit",
      timeZone: timezone,
    },
    shortDateTime: {
      year: "2-digit",
      month: "2-digit",
      day: "2-digit",
      hour,
      minute: "2-digit",
      timeZone: timezone,
    },
    shortDateTimeWithWeekday: {
      weekday: "short",
      year: "2-digit",
      month: "2-digit",
      day: "2-digit",
      hour,
      minute: "2-digit",
      timeZone: timezone,
    },
  };
}

function englishFormats(timezone: string): LocaleDateTimeFormats {
  return {
    numeric: {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      timeZone: timezone,
    },
    short: {
      year: "numeric",
      month: "short",
      day: "numeric",
      timeZone: timezone,
    },
    // `short` without the year, for the narrowest tier (#12691). The year is
    // the first thing a phone can spare: the row still carries the full date
    // in its tooltip.
    dayMonth: {
      month: "short",
      day: "numeric",
      timeZone: timezone,
    },
    // The start of a range whose end carries the year: "02.11. – 06.11.2026".
    numericDayMonth: {
      month: "2-digit",
      day: "2-digit",
      timeZone: timezone,
    },
    medium: {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
      timeZone: timezone,
    },
    long: {
      year: "numeric",
      month: "short",
      day: "numeric",
      weekday: "short",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
      timeZone: timezone,
    },
    monthYear: {
      year: "numeric",
      month: "long",
      timeZone: timezone,
    },
    time: {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: timezone,
    },
    timeShort: {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: timezone,
    },
    dayMonthTime: {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: timezone,
    },
    dateLong: {
      year: "numeric",
      month: "long",
      day: "2-digit",
      timeZone: timezone,
    },
    webinarDate: {
      weekday: "long",
      day: "2-digit",
      month: "long",
      year: "numeric",
      timeZone: timezone,
    },
    webinarMedium: {
      day: "numeric",
      month: "long",
      hour: "2-digit",
      minute: "2-digit",
      timeZoneName: "short",
      hour12: true,
      timeZone: timezone,
    },
    webinarFull: {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      timeZoneName: "short",
      hour12: true,
      timeZone: timezone,
    },
    ...sharedFormats(timezone, "numeric"),
  };
}

// German, French and Dutch: day-first dates and a 24-hour clock. The locale
// supplies the separators (09.07.2025, 09/07/2025, 09-07-2025).
function dayFirstFormats(timezone: string): LocaleDateTimeFormats {
  return {
    numeric: {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      timeZone: timezone,
    },
    short: {
      year: "numeric",
      month: "short",
      day: "numeric",
      timeZone: timezone,
    },
    // `short` without the year, for the narrowest tier (#12691). The year is
    // the first thing a phone can spare: the row still carries the full date
    // in its tooltip.
    dayMonth: {
      month: "short",
      day: "numeric",
      timeZone: timezone,
    },
    // The start of a range whose end carries the year: "02.11. – 06.11.2026".
    numericDayMonth: {
      month: "2-digit",
      day: "2-digit",
      timeZone: timezone,
    },
    medium: {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "numeric",
      minute: "numeric",
      hour12: false,
      timeZone: timezone,
    },
    long: {
      year: "numeric",
      month: "short",
      day: "numeric",
      weekday: "short",
      hour: "numeric",
      minute: "numeric",
      hour12: false,
      timeZone: timezone,
    },
    monthYear: {
      year: "numeric",
      month: "long",
      timeZone: timezone,
    },
    time: {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: timezone,
    },
    timeShort: {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: timezone,
    },
    dayMonthTime: {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: timezone,
    },
    dateLong: {
      year: "numeric",
      month: "long",
      day: "2-digit",
      timeZone: timezone,
    },
    webinarDate: {
      weekday: "long",
      day: "2-digit",
      month: "long",
      year: "numeric",
      timeZone: timezone,
    },
    webinarMedium: {
      day: "numeric",
      month: "long",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: timezone,
    },
    webinarFull: {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: timezone,
    },
    ...sharedFormats(timezone, "2-digit"),
  };
}

export function buildDateTimeFormats(
  timezone: string = DEFAULT_TIMEZONE
): IntlDateTimeFormats {
  return {
    en: englishFormats(timezone),
    de: dayFirstFormats(timezone),
    fr: dayFirstFormats(timezone),
    nl: dayFirstFormats(timezone),
  };
}

export const datetimeFormats: IntlDateTimeFormats = buildDateTimeFormats();
